import { Flex, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  HOME_FONT_STACK,
  homeColor,
  homeSize,
  homeText,
  homeWelcomeGradient
} from "../home-theme.ts"
import { type UserInfo } from "../../LoginPage/types/auth.types.ts"
import {
  DashboardHeaderSearch,
  type DashboardTableFilterKey
} from "./DashboardHeaderSearch.tsx"
import { HomeWelcomeArt } from "./HomeWelcomeArt.tsx"

const { Text } = Typography

interface DashboardHeaderProps {
  user: UserInfo | null
  canSearch: boolean
  canCreate: boolean
  tableFilterKey: DashboardTableFilterKey | null
  /** The My View / Edit Layout pair, which sits under the subtitle. */
  viewControls?: React.ReactNode
  /** Export, which sits under the search + New Project group. */
  exportControl?: React.ReactNode
}

export function DashboardHeader({
  user,
  canCreate,
  canSearch,
  tableFilterKey,
  viewControls,
  exportControl
}: DashboardHeaderProps) {
  const { t } = useTranslation()
  const name = user?.first_name

  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        background: homeWelcomeGradient,
        border: `1px solid ${homeColor.border}`,
        borderRadius: homeSize.cardRadius,
        fontFamily: HOME_FONT_STACK
      }}
    >
      <HomeWelcomeArt />
      <Flex
        gap={16}
        wrap="wrap"
        align="flex-start"
        justify="space-between"
        className={"max-lg:flex-col max-lg:items-start"}
        style={{ position: "relative", padding: "18px 20px 16px" }}
      >
        <div className={"min-w-0"}>
          <h1
            className="m-0 text-start"
            style={{ ...homeText.welcomeTitle, color: homeColor.ink }}
          >
            {name
              ? t("dashboard.greeting", "Welcome, {{name}}!", { name })
              : t("dashboard.greetingNoName", "Welcome!")}
          </h1>
          <Text
            style={{ ...homeText.welcomeSubtitle, color: homeColor.inkMuted }}
          >
            {t(
              "dashboard.subtitle",
              "Here's a snapshot of what's happening today."
            )}
          </Text>
          {viewControls ? (
            <div style={{ marginTop: 14 }}>{viewControls}</div>
          ) : null}
        </div>

        {/* Search + New Project sit together at the top right, with Export
            directly under them — the reference's own grouping, and close
            enough that neither reads as belonging to the greeting. */}
        <Flex vertical align="flex-end" gap={10} className={"max-lg:items-start"}>
          {(canSearch || canCreate) && tableFilterKey && (
            <DashboardHeaderSearch
              canCreate={canCreate}
              tableFilterKey={tableFilterKey}
            />
          )}
          {exportControl}
        </Flex>
      </Flex>
    </div>
  )
}
